// src/pages/Admin/components/ConciergeExcelUploadModal.jsx
import React, { useRef, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import Hangul from "hangul-js"; // 👈 검색 라이브러리

const MAX_FILES = 5;

/** 공통 검색 유틸 (한글/숫자/영어 모두 대응) */
const matchesText = (text, query) => {
    const q = query?.toString().trim().toLowerCase();
    if (!q) return true;

    const t = (text ?? "").toString().toLowerCase();
    if (!t) return false;

    // hangul-js: 한글 + 초성 / 숫자 / 영어까지 모두 한 번에 처리
    return Hangul.search(t, q) >= 0;
};

const ConciergeExcelUploadModal = ({
    open,
    onClose,
    itemsCount = 0,
    onFilesSelected, // (FileList | File[]) => void (옵션),
    onSubmit,
}) => {
    const fileInputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    // 업로드된 파일 + 각 파일에서 파싱된 행들
    const [uploadedFiles, setUploadedFiles] = useState([]); // {id,file,name,size,rows: any[]}
    const [activeFileId, setActiveFileId] = useState(null); // 왼쪽 리스트에서 선택한 파일

    // 🔹 새로 추가: "열려 있는 탭" 목록
    const [openFileIds, setOpenFileIds] = useState([]); // string[] (file.id 리스트)

    // 선택된 행 (fileId:rowIndex 형태의 key 배열)
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // 정렬 상태
    const [sortState, setSortState] = useState({ key: null, dir: "asc" });

    // 검색어
    const [searchTerm, setSearchTerm] = useState("");

    /** 닫기 + 내부 상태 초기화 */
    const handleClose = () => {
        setUploadedFiles([]);
        setActiveFileId(null);
        setSelectedRowKeys([]);
        setDragOver(false);
        setSortState({ key: null, dir: "asc" });
        setSearchTerm("");
        onClose?.();
        setOpenFileIds([]);
    };

    /** 엑셀/CSV 한 개 파싱 */
    const parseExcelFile = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const wb = XLSX.read(data, { type: "array" });
                    const sheet = wb.Sheets[wb.SheetNames[0]];
                    const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
                    resolve(json); // [{컬럼명: 값, ...}, ...]
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });

    /** 여러 파일 추가 공통 처리 */
    const handleAddFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;

        const remain = MAX_FILES - uploadedFiles.length;
        if (remain <= 0) {
            alert(`엑셀 파일은 최대 ${MAX_FILES}개까지 업로드할 수 있습니다.`);
            return;
        }
        const targetFiles = files.slice(0, remain);

        try {
            const parsedList = await Promise.all(
                targetFiles.map(async (file) => {
                    const rows = await parseExcelFile(file);
                    return {
                        id: `${file.name}-${file.lastModified}-${Math.random()
                            .toString(36)
                            .slice(2)}`,
                        file,
                        name: file.name,
                        size: file.size,
                        rows,
                    };
                })
            );

            setUploadedFiles((prev) => [...prev, ...parsedList]);

            // 🔹 새 파일들 탭에 등록
            setOpenFileIds((prev) => {
                const next = [...prev];
                parsedList.forEach((f) => {
                    if (!next.includes(f.id)) next.push(f.id);
                });
                return next;
            });

            setActiveFileId((prevId) => prevId ?? parsedList[0]?.id ?? null);
            setSelectedRowKeys([]);

            onFilesSelected?.(targetFiles);
        } catch (e) {
            console.error("엑셀 파싱 오류:", e);
            alert("엑셀 파일을 읽는 중 오류가 발생했습니다.");
        }
    };

    /** 파일 제거 */
    const handleRemoveFile = (id) => {
        // 파일 목록에서 제거
        setUploadedFiles((prev) => {
            const remain = prev.filter((f) => f.id !== id);

            // active 탭이 이 파일이면, 남아 있는 탭 또는 파일 중 하나로 포커스 이동
            setActiveFileId((current) => {
                if (current !== id) return current;

                // 먼저 열린 탭 중에서 선택
                const nextFromOpen = openFileIds.filter((fid) => fid !== id)[0];
                return nextFromOpen ?? remain[0]?.id ?? null;
            });

            return remain;
        });

        // 🔹 열린 탭 목록에서도 제거
        setOpenFileIds((prev) => prev.filter((fid) => fid !== id));

        // 선택 행 초기화
        setSelectedRowKeys([]);
    };


    /** 드래그/드롭 핸들러들 */
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };
    const handleDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);
        const files = e.dataTransfer?.files;
        await handleAddFiles(files);
    };

    const handleFileChange = async (e) => {
        const files = e.target.files;
        await handleAddFiles(files);
        e.target.value = "";
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    /** 엑셀 컬럼 → 화면용 필드 매핑 */
    const mapRow = (row) => {
        const pick = (candidates) => {
            for (const key of candidates) {
                if (row[key] !== undefined && row[key] !== "") return row[key];
            }
            return "";
        };

        return {
            name: pick(["점주이름", "이름"]),
            phone: pick(["휴대폰 번호", "휴대폰번호", "전화번호", "연락처", "phone"]),
            storeName: pick(["가게명", "가게 이름"]),
            roadName: pick(["도로명 주소", "주소", "road_name"]),
            menu1: pick(["주요 메뉴 1", "메뉴1", "대표메뉴1"]),
            menu2: pick(["주요 메뉴 2", "메뉴2", "대표메뉴2"]),
            menu3: pick(["주요 메뉴 3", "메뉴3", "대표메뉴3"]),
            blog: pick(["블로그"]),
            instagram : pick(["인스타"]),
            recoProduct : pick(["추천 상품 1"]),
            recoReason : pick(["추천이유 1"]),
            expectEffect : pick(["기대효과 1"]),
            additionalSuggest : pick(["추가제안 1"])
        };
    };

    /** 오른쪽 테이블에 기본으로 쓸 행들 (fileId / rowIndex 포함) */
    const viewRows = useMemo(() => {
        const list = [];
        if (activeFileId) {
            const file = uploadedFiles.find((f) => f.id === activeFileId);
            (file?.rows || []).forEach((raw, idx) => {
                list.push({
                    fileId: file.id,
                    rowIndex: idx,
                    row: mapRow(raw),
                });
            });
        } else {
            uploadedFiles.forEach((file) => {
                (file.rows || []).forEach((raw, idx) => {
                    list.push({
                        fileId: file.id,
                        rowIndex: idx,
                        row: mapRow(raw),
                    });
                });
            });
        }
        return list;
    }, [uploadedFiles, activeFileId]);

    /** 검색 필터 (한글/초성/영어/숫자 모두 Hangul.search로 처리) */
    const filteredRows = useMemo(() => {
        const q = searchTerm.trim();
        if (!q) return viewRows;

        return viewRows.filter(({ row }) => {
            const fields = [
                row.name,
                row.phone,
                row.storeName,
                row.roadName,
                row.menu1,
                row.menu2,
                row.menu3,
            ];
            return fields.some((f) => matchesText(f, q));
        });
    }, [viewRows, searchTerm]);

    /** 정렬 적용 */
    const sortedRows = useMemo(() => {
        if (!sortState.key) return filteredRows;

        const { key, dir } = sortState;
        const sorted = [...filteredRows].sort((a, b) => {
            const av = (a.row[key] || "").toString();
            const bv = (b.row[key] || "").toString();
            const cmp = av.localeCompare(bv, "ko", { sensitivity: "base" });
            return dir === "asc" ? cmp : -cmp;
        });
        return sorted;
    }, [filteredRows, sortState]);

    /** 정렬 헤더 클릭 */
    const toggleSort = (key) => {
        setSortState((prev) => {
            if (prev.key === key) {
                return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
            }
            return { key, dir: "asc" };
        });
    };

    /** key 생성 (fileId:rowIndex) */
    const getRowKey = (item) => `${item.fileId}:${item.rowIndex}`;

    /** 체크박스 토글 */
    const toggleRow = (rowKey) => {
        setSelectedRowKeys((prev) =>
            prev.includes(rowKey)
                ? prev.filter((k) => k !== rowKey)
                : [...prev, rowKey]
        );
    };

    const allChecked =
        sortedRows.length > 0 && selectedRowKeys.length === sortedRows.length;

    const toggleAll = (checked) => {
        if (checked) {
            setSelectedRowKeys(sortedRows.map((r) => getRowKey(r)));
        } else {
            setSelectedRowKeys([]);
        }
    };

    /** 선택 삭제 */
    const handleDeleteSelected = () => {
        if (selectedRowKeys.length === 0) {
            alert("삭제할 행을 선택해 주세요.");
            return;
        }
        if (!window.confirm("선택한 행을 삭제하시겠습니까?")) return;

        setUploadedFiles((prev) =>
            prev.map((file) => {
                const newRows = file.rows.filter(
                    (_row, idx) => !selectedRowKeys.includes(`${file.id}:${idx}`)
                );
                return { ...file, rows: newRows };
            })
        );
        setSelectedRowKeys([]);
    };

    /** 파일 용량 표시 (KB) */
    const formatSize = (bytes) => `${Math.round(bytes / 1024)}KB`;


    // 서버 제출
    const handleSubmit = async () => {
        // 1) 업로드된 전체/선택된 행을 payload로 만들어서
        const rows = [];

        uploadedFiles.forEach((file) => {
            (file.rows || []).forEach((raw) => {
                const mapped = mapRow(raw); // { name, phone, storeName, roadName, menu1, ... }
                const normalizedPhone = (mapped.phone || "").toString().replace(/\D/g, "");

                // 🔹 여기서 snake_case로 변환
                rows.push({
                    name: mapped.name ?? "",
                    phone: normalizedPhone ?? "",
                    store_name: mapped.storeName ?? "",
                    road_name: mapped.roadName ?? "",
                    menu_1: mapped.menu1 ?? "",
                    menu_2: mapped.menu2 ?? "",
                    menu_3: mapped.menu3 ?? "",
                    blog : mapped.blog ?? "",
                    instagram : mapped.instagram ?? "",
                    reco_product : mapped.recoProduct ?? "",
                    reco_reason : mapped.recoReason ?? "",
                    expect_effect : mapped.expectEffect ?? "",
                    additional_suggest : mapped.additionalSuggest ?? "",
                });
            });
        });

        if (!rows.length) {
            alert("등록할 데이터가 없습니다.");
            return;
        }

        try {
            // 🔹 부모로는 snake_case로 변환된 rows만 넘김
            await onSubmit?.(rows);
            handleClose(); // 성공 시 모달 닫기 + 초기화
        } catch (e) {
            console.error(e);
            alert("컨시어지 등록 중 오류가 발생했습니다.");
        }
    };



    if (!open) return null;

    return (
        <>
            {/* dimmed */}
            <div className="fixed inset-0 z-[60] bg-black/30" onClick={handleClose} />

            {/* modal */}
            <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
                <div className="relative w-full h-[90vh] rounded-3xl bg-white shadow-xl p-6 md:p-8 flex flex-col">
                    {/* 헤더 */}
                    <div className="flex items-start justify-between pb-4">
                        <h2 className="text-lg md:text-xl font-semibold">컨시어지 등록</h2>
                        <button
                            type="button"
                            onClick={handleClose}
                            className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-neutral-100 text-neutral-400 text-xl leading-none"
                        >
                            ×
                        </button>
                    </div>

                    {/* 본문 */}
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-[300px_minmax(0,1fr)] gap-6 md:gap-8 overflow-hidden pt-1">
                        {/* 왼쪽: 업로드 영역 */}
                        <div className="flex flex-col gap-6">
                            {/* 업로드 타이틀 & 드롭존 */}
                            <div>
                                <p className="text-sm font-medium mb-3">업로드</p>

                                <div
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onClick={openFileDialog}
                                    className={`
                    flex flex-col items-center justify-center
                    rounded-2xl border-2 border-dashed px-4 py-10
                    text-center cursor-pointer transition
                    ${dragOver
                                            ? "border-[#7864F9] bg-violet-50/40"
                                            : "border-[#E2E4F0] bg-[#F7F8FB]"
                                        }
                  `}
                                >
                                    <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm text-neutral-400 text-xl">
                                        📁
                                    </div>
                                    <div className="text-sm font-medium mb-1">
                                        엑셀 파일 끌어다 놓기
                                    </div>
                                    <div className="text-[11px] text-neutral-400 mb-2">(10 MB)</div>
                                    <div className="text-xs text-[#7864F9] font-medium">
                                        내 장치 선택
                                    </div>
                                </div>
                            </div>

                            {/* 업로드된 엑셀 파일 리스트 */}
                            <div className="flex-1 min-h-0">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium">업로드 엑셀 파일</p>
                                    <span className="text-[11px] text-neutral-400">
                                        최대 {MAX_FILES}개
                                    </span>
                                </div>

                                <div className="h-full rounded-2xl border border-[#E2E4F0] bg-[#F7F8FB] px-2 py-2 overflow-y-auto">
                                    {uploadedFiles.length === 0 ? (
                                        <div className="h-full flex items-center justify-center px-4">
                                            <p className="text-xs text-neutral-400 text-center">
                                                업로드된 엑셀 파일이 없습니다.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {uploadedFiles.map((f) => {
                                                const isActive = f.id === activeFileId;
                                                return (
                                                    <button
                                                        key={f.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveFileId(f.id);
                                                            setSelectedRowKeys([]);
                                                            setOpenFileIds((prev) =>
                                                                prev.includes(f.id) ? prev : [...prev, f.id]
                                                            );
                                                        }}
                                                        className={`w-full flex items-center justify-between rounded-xl px-3 py-2 border text-left transition -ml-px first:ml-0
                              ${isActive
                                                                ? "bg-[#ECEBFF] border-[#7864F9]"
                                                                : "bg-white border-[#E2E4F0] hover:bg-[#F5F6FF]"
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#F7F8FB] text-neutral-400 text-sm">
                                                                📄
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="text-xs font-medium text-neutral-800 truncate">
                                                                    {f.name}
                                                                </div>
                                                                <div className="text-[11px] text-neutral-400">
                                                                    {formatSize(f.size)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveFile(f.id);
                                                            }}
                                                            className="ml-2 text-neutral-300 hover:text-neutral-500 text-sm"
                                                        >
                                                            ×
                                                        </button>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 숨겨진 input */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                multiple
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </div>

                        {/* 오른쪽: 컨시어지 목록 */}
                        <div className="flex flex-col min-h-0">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-medium mb-3">
                                    컨시어지 목록{" "}
                                    <span className="ml-1 text-xs text-neutral-400">
                                        총 {itemsCount}건
                                    </span>
                                </p>

                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={handleDeleteSelected}
                                        className="flex items-center gap-1 border border-rose-100 bg-rose-50/70 px-3 py-1 text-[11px] text-rose-300 font-medium"
                                    >
                                        🗑 선택 삭제
                                    </button>
                                    <input
                                        type="text"
                                        placeholder="검색어를 입력해주세요"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="h-7 w-[150px] border border-[#E2E4F0] px-3 text-xs text-neutral-700 placeholder:text-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#7864F9]"
                                    />
                                </div>
                            </div>
                            {/* 🔹 새로 추가: 파일별 탭 영역 */}
                            {openFileIds.length > 0 && (
                                <div className="mb-1 -mt-1 px-1 border-b border-[#E2E4F0] flex items-end overflow-x-auto">
                                    {openFileIds.map((fileId) => {
                                        const f = uploadedFiles.find((uf) => uf.id === fileId);
                                        if (!f) return null; // 혹시 파일이 삭제됐으면 스킵

                                        const isActive = f.id === activeFileId;
                                        const rowCount = (f.rows || []).length;

                                        return (
                                            <button
                                                key={f.id}
                                                type="button"
                                                onClick={() => {
                                                    setActiveFileId(f.id);
                                                    setSelectedRowKeys([]);
                                                }}
                                                className={`
            relative flex items-center max-w-[220px] min-w-[140px]
            px-3 py-1.5 text-[11px] whitespace-nowrap
            rounded-t-2xl border border-b-0
            transition-colors
            ${isActive
                                                        ? "bg-white text-neutral-800 border-[#D0D3E2] z-10"
                                                        : "bg-[#F5F7FB] text-neutral-500 border-transparent hover:bg-[#ECEFF9]"
                                                    }
          `}
                                            >
                                                {/* 파일명 (크롬 탭처럼 좌측 정렬 + 말줄임) */}
                                                <span className="truncate">
                                                    {f.name}
                                                </span>

                                                {/* (15) 부분 – 파일명과 살짝 붙여서 */}
                                                <span className="ml-1 text-[10px] text-neutral-400">
                                                    ({rowCount})
                                                </span>

                                                {/* X 버튼 – 아주 작게, 우측 끝 */}
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setOpenFileIds((prev) => {
                                                            const next = prev.filter((id) => id !== f.id);

                                                            // 닫는 탭이 active였다면, 다음 탭으로 포커스 이동
                                                            if (activeFileId === f.id) {
                                                                setActiveFileId(next[0] ?? null);
                                                            }

                                                            return next;
                                                        });
                                                    }}
                                                    className="ml-2 text-[11px] text-neutral-300 hover:text-neutral-500 cursor-pointer"
                                                >
                                                    ×
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}


                            <div className="flex-1 rounded-2xl border border-[#E2E4F0] bg-[#F9FAFF] overflow-hidden">
                                <div className="h-full overflow-auto bg-white rounded-2xl">
                                    <table className="w-full text-[11px] border-collapse">
                                        <thead className="bg-[#F1F3FF] text-neutral-500 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-3 py-2 w-8 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={allChecked}
                                                        onChange={(e) => toggleAll(e.target.checked)}
                                                    />
                                                </th>
                                                <th
                                                    className="px-3 py-2 text-left cursor-pointer select-none"
                                                    onClick={() => toggleSort("name")}
                                                >
                                                    <span className="inline-flex items-center gap-1">
                                                        이름
                                                        {sortState.key === "name" && (
                                                            <span>{sortState.dir === "asc" ? "▲" : "▼"}</span>
                                                        )}
                                                    </span>
                                                </th>
                                                <th className="px-3 py-2 text-left">휴대폰 번호</th>
                                                <th
                                                    className="px-3 py-2 text-left cursor-pointer select-none"
                                                    onClick={() => toggleSort("storeName")}
                                                >
                                                    <span className="inline-flex items-center gap-1">
                                                        가게 이름
                                                        {sortState.key === "storeName" && (
                                                            <span>{sortState.dir === "asc" ? "▲" : "▼"}</span>
                                                        )}
                                                    </span>
                                                </th>
                                                <th className="px-3 py-2 text-left">도로명 주소</th>
                                                <th className="px-3 py-2 text-left">주요 메뉴 1</th>
                                                <th className="px-3 py-2 text-left">주요 메뉴 2</th>
                                                <th className="px-3 py-2 text-left">주요 메뉴 3</th>
                                                <th className="px-3 py-2 text-left">블로그</th>
                                                <th className="px-3 py-2 text-left">인스타</th>
                                                <th className="px-3 py-2 text-left">추천 상품 1</th>
                                                <th className="px-3 py-2 text-left">추천이유 1</th>
                                                <th className="px-3 py-2 text-left">기대효과 1</th>
                                                <th className="px-3 py-2 text-left">추가제안 1</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedRows.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={8}
                                                        className="px-3 py-6 text-center text-neutral-400"
                                                    >
                                                        업로드한 엑셀에서 불러온 데이터가 없습니다.
                                                    </td>
                                                </tr>
                                            ) : (
                                                sortedRows.map((item) => {
                                                    const { row } = item;
                                                    const key = getRowKey(item);
                                                    const checked = selectedRowKeys.includes(key);
                                                    return (
                                                        <tr
                                                            key={key}
                                                            className="border-t border-[#F0F1F5]"
                                                        >
                                                            <td className="px-3 py-2 text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => toggleRow(key)}
                                                                />
                                                            </td>
                                                            <td className="px-3 py-2">{row.name}</td>
                                                            <td className="px-3 py-2">{row.phone}</td>
                                                            <td className="px-3 py-2">{row.storeName}</td>
                                                            <td className="px-3 py-2">{row.roadName}</td>
                                                            <td className="px-3 py-2">{row.menu1}</td>
                                                            <td className="px-3 py-2">{row.menu2}</td>
                                                            <td className="px-3 py-2">{row.menu3}</td>
                                                            <td className="px-3 py-2">{row.blog}</td>
                                                            <td className="px-3 py-2">{row.instagram}</td>
                                                            <td className="px-3 py-2">{row.recoProduct}</td>
                                                            <td className="px-3 py-2">{row.recoReason}</td>
                                                            <td className="px-3 py-2">{row.expectEffect}</td>
                                                            <td className="px-3 py-2">{row.additionalSuggest}</td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 하단 버튼 */}
                    <div className="mt-6 flex justify-end gap-2">

                        <button
                            type="button"
                            className="h-10 px-6 rounded-xl bg-[#7864F9] text-white text-sm font-medium hover:bg-violet-600"
                            onClick={handleSubmit}
                        >
                            저장
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConciergeExcelUploadModal;
